import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { ROLE_PERMISSIONS } from "../constants/roles";
import { signin } from "../api";

/*
 * AuthContext
 * -----------
 * Holds the currently signed-in user (or null), token and helpers.
 * Session is persisted to localStorage under `ih_auth`.
 */

const STORAGE_KEY = "ih_auth";
const AUTH_FLASH_KEY = "ih_auth_flash";
const SESSION_EXPIRED_FLASH = "Session expired, please sign in again.";

function setSessionExpiredFlash() {
  if (typeof window === "undefined") return;
  sessionStorage.setItem(AUTH_FLASH_KEY, SESSION_EXPIRED_FLASH);
}

function clearSessionExpiredFlash() {
  if (typeof window === "undefined") return;
  sessionStorage.removeItem(AUTH_FLASH_KEY);
}

function toExpiryMs(expiresAt) {
  if (!expiresAt) return null;

  if (typeof expiresAt === "number" && Number.isFinite(expiresAt)) {
    return expiresAt > 1e12 ? expiresAt : expiresAt * 1000;
  }

  if (typeof expiresAt === "string") {
    const numeric = Number(expiresAt);
    if (Number.isFinite(numeric) && expiresAt.trim() !== "") {
      return numeric > 1e12 ? numeric : numeric * 1000;
    }

    const parsed = Date.parse(expiresAt);
    return Number.isNaN(parsed) ? null : parsed;
  }

  return null;
}

function isTokenExpired(expiresAt) {
  const expiryMs = toExpiryMs(expiresAt);
  if (!expiryMs) return false;
  return expiryMs <= Date.now();
}

function normalizeSessionUser(user) {
  if (!user) return null;
  return {
    id: user.id,
    email: user.email,
    name: user.name,
    role: user.role,
    ventureId: user.linked_venture ?? user.linkedVenture ?? null,
  };
}

function getInitialAuthState() {
  const fallback = {
    user: null,
    token: null,
    expiresAt: null,
    isAuthenticated: false,
    isLoading: false,
  };

  const saved = localStorage.getItem(STORAGE_KEY);
  if (!saved) return fallback;

  try {
    const parsed = JSON.parse(saved);
    const user = normalizeSessionUser(parsed?.user);
    const token = parsed?.token ?? null;
    const expiresAt = parsed?.expiresAt ?? null;

    if (!user || !token || isTokenExpired(expiresAt)) {
      if (isTokenExpired(expiresAt)) {
        setSessionExpiredFlash();
      }
      localStorage.removeItem(STORAGE_KEY);
      return fallback;
    }

    return {
      user,
      token,
      expiresAt,
      isAuthenticated: true,
      isLoading: false,
    };
  } catch {
    localStorage.removeItem(STORAGE_KEY);
    return fallback;
  }
}

// Create the context. `undefined` default lets useAuth detect missing provider.
const AuthContext = createContext(undefined);

export function AuthProvider({ children }) {
  const [state, setState] = useState(getInitialAuthState);

  /*
   * login() — returns true on success, false on bad credentials.
   * Simulates a network delay so the UI spinner is visible.
   */
  const login = async (email, password) => {
    const result = await signin({ email: email.trim(), password });
    if (result.ok && result.user?.role) {
      clearSessionExpiredFlash();
      const user = normalizeSessionUser(result.user);
      const authState = {
        user,
        token: result.token,
        expiresAt: result.expiresAt,
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(authState));
      setState({
        ...authState,
        isAuthenticated: true,
        isLoading: false,
      });
      return { user, error: null };
    }

    return {
      user: null,
      error: result.error ?? "Invalid email or password",
    };
  };

  // Clear session from memory + storage.
  const logout = useCallback(() => {
    clearSessionExpiredFlash();
    localStorage.removeItem(STORAGE_KEY);
    setState({
      user: null,
      token: null,
      expiresAt: null,
      isAuthenticated: false,
      isLoading: false,
    });
  }, []);

  useEffect(() => {
    if (!state.isAuthenticated) return;

    const expiryMs = toExpiryMs(state.expiresAt);
    if (!expiryMs) return;

    const timeoutMs = expiryMs - Date.now();
    if (timeoutMs <= 0) {
      setSessionExpiredFlash();
      localStorage.removeItem(STORAGE_KEY);
      setState({
        user: null,
        token: null,
        expiresAt: null,
        isAuthenticated: false,
        isLoading: false,
      });
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setSessionExpiredFlash();
      localStorage.removeItem(STORAGE_KEY);
      setState({
        user: null,
        token: null,
        expiresAt: null,
        isAuthenticated: false,
        isLoading: false,
      });
    }, timeoutMs);

    return () => window.clearTimeout(timeoutId);
  }, [state.isAuthenticated, state.expiresAt, logout]);

  // Permission check used by sidebars / page guards.
  const hasPermission = (permission) => {
    if (!state.user) return false;
    return ROLE_PERMISSIONS[state.user.role]?.includes(permission) ?? false;
  };

  return (
    <AuthContext.Provider value={{ ...state, login, logout, hasPermission }}>
      {children}
    </AuthContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const ctx = useContext(AuthContext);
  if (ctx === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return ctx;
}
